import { useState, useCallback, useEffect, useLayoutEffect } from 'react';
import { measureScrollListMaxHeight } from '../utils/mobileSearchInput';

/**
 * Keeps a scrollable list below a search field within the visible viewport (keyboard-aware).
 * @returns {number | null} px max-height for the list container, or null before first measure
 */
export function useKeyboardAwareScrollMaxHeight(
  anchorRef,
  { enabled = true, minHeight = 80, padding = 12, maxPreferred = 480 } = {}
) {
  const [maxHeight, setMaxHeight] = useState(null);

  const update = useCallback(() => {
    if (!enabled || !anchorRef?.current) return;
    const next = measureScrollListMaxHeight(anchorRef.current, { minHeight, padding, maxPreferred });
    if (next != null) setMaxHeight(next);
  }, [anchorRef, enabled, minHeight, padding, maxPreferred]);

  useLayoutEffect(() => {
    update();
  }, [update]);

  useEffect(() => {
    if (!enabled) return;

    let raf = 0;
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(update);
    };

    window.addEventListener('resize', schedule);
    window.visualViewport?.addEventListener('resize', schedule);
    window.visualViewport?.addEventListener('scroll', schedule);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', schedule);
      window.visualViewport?.removeEventListener('resize', schedule);
      window.visualViewport?.removeEventListener('scroll', schedule);
    };
  }, [enabled, update]);

  return maxHeight;
}

export default useKeyboardAwareScrollMaxHeight;
